/**
 * @fileoverview "Suggested links" section for detail / anchor panels
 * @module components/jobs/phases/gather/summarization/shared-panels/SuggestedLinksSection
 *
 * Purpose:
 * - Surface weak-confidence link candidates the deterministic linker has
 *   suggested for the currently open detail / anchor record. Each row gets
 *   Accept / Dismiss controls so the practitioner can promote or reject the
 *   suggestion without leaving the panel.
 *
 * Dependencies:
 * - `useJobLinkSuggestions`, `useJobRecordIndex`, `useLinkMutations` from
 *   `linkShared` (polymorphic recordLinks backend).
 *
 * Used by:
 * - Every detail / anchor panel (Asset, Creditor, Security Interest, Loan,
 *   ATO/SRO), placed above `LinkedRecordsSection`.
 */

import { useMemo } from "react";
import { Check, Link2, Sparkles, X } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import {
  RECORD_TYPE_ICON,
  RECORD_TYPE_LABEL,
  RECORD_TYPE_BADGE_COLOR,
  isAnchorType,
  useJobLinkSuggestions,
  useJobRecordIndex,
  useLinkMutations,
  type JobLinkSuggestion,
  type LinkAnchor,
  type RecordRef,
  type RecordType,
} from "./linkShared";

interface SuggestedLinksSectionProps {
  jobId: Id<"jobs">;
  anchor: LinkAnchor;
  onOpenLinked: (type: RecordType, id: string) => void;
  isViewer?: boolean;
}

export function SuggestedLinksSection({
  jobId,
  anchor,
  onOpenLinked,
  isViewer,
}: SuggestedLinksSectionProps) {
  const suggestions = useJobLinkSuggestions(jobId);
  const records = useJobRecordIndex(jobId);
  const { accept, dismiss } = useLinkMutations();

  const viewerIsAnchor = isAnchorType(anchor.type);

  // Filter to suggestions touching this open record on whichever side it sits.
  const mySuggestions: JobLinkSuggestion[] = useMemo(() => {
    return suggestions.filter((s) =>
      viewerIsAnchor
        ? s.anchorType === anchor.type && s.anchorId === anchor.id
        : s.detailType === anchor.type && s.detailId === anchor.id,
    );
  }, [suggestions, anchor.type, anchor.id, viewerIsAnchor]);

  if (mySuggestions.length === 0) return null;

  const rows = mySuggestions.map((s) => {
    const counterpartId = viewerIsAnchor ? s.detailId : s.anchorId;
    const ref: RecordRef | undefined = records.byId.get(counterpartId);
    return { suggestion: s, ref };
  });

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
        <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
          Suggested links
        </h4>
        <Badge variant="secondary" size="sm">
          {rows.length}
        </Badge>
      </div>

      <div className="space-y-1.5">
        {rows.map(({ suggestion, ref }) => {
          const RowIcon = ref ? RECORD_TYPE_ICON[ref.type] : Link2;
          return (
            <div
              key={suggestion._id}
              className="flex items-center gap-2 rounded-lg border border-amber-300/40 bg-amber-50/40 px-2 py-1.5 dark:border-amber-700/40 dark:bg-amber-950/20"
            >
              <button
                onClick={() => ref && onOpenLinked(ref.type, ref.id)}
                disabled={!ref}
                className={cn(
                  "min-w-0 flex-1 text-left",
                  ref && "transition-opacity hover:opacity-70",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <RowIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">
                    {ref?.title ?? "Unavailable record"}
                  </span>
                  {ref && (
                    <Badge
                      color={RECORD_TYPE_BADGE_COLOR[ref.type]}
                      variant="soft"
                      size="sm"
                      className="shrink-0"
                    >
                      {RECORD_TYPE_LABEL[ref.type]}
                    </Badge>
                  )}
                </div>
                <div className="truncate pl-[18px] text-xs text-muted-foreground">
                  {suggestion.reason}
                </div>
              </button>

              {!isViewer && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() =>
                      accept.mutate({
                        suggestionId: suggestion._id as Id<"linkSuggestions">,
                      })
                    }
                    title="Accept suggestion"
                    className="text-emerald-600 transition-colors hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() =>
                      dismiss.mutate({
                        suggestionId: suggestion._id as Id<"linkSuggestions">,
                      })
                    }
                    title="Dismiss suggestion"
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
